import React, { forwardRef } from "react";

import { LoadingOutlined } from "@ant-design/icons";
import { Slot } from "@radix-ui/react-slot";
import { cva, VariantProps } from "class-variance-authority";

import { buttonStyles } from "./Button.css";

// `variant` mirrors the Type axis of Button / Basic in UNIFORM V2, plus a
// `destructive` the design has no name for (it draws as Primary + Danger).
// `ghost` and `danger` below are the design's own boolean axes.
export const buttonStyle = cva(buttonStyles.base, {
    variants: {
        variant: {
            default: buttonStyles.variants.default,
            primary: buttonStyles.variants.primary,
            destructive: buttonStyles.variants.destructive,
            dashed: buttonStyles.variants.dashed,
            text: buttonStyles.variants.text,
            link: buttonStyles.variants.link,
        },
        size: {
            default: buttonStyles.sizes.default,
            small: buttonStyles.sizes.small,
            large: buttonStyles.sizes.large,
            icon: buttonStyles.sizes.icon,
            nosize: buttonStyles.sizes.nosize,
        },
        block: {
            true: buttonStyles.block,
            false: "",
        },
        ghost: {
            true: "",
            false: "",
        },
        danger: {
            true: "",
            false: "",
        },
    },
    // Every compound is mutually exclusive, so precedence never depends on the
    // order the classes happen to land in the stylesheet.
    compoundVariants: [
        // The design draws Ghost for the outlined types only — Text and Link ignore it.
        {
            variant: ["default", "dashed"],
            ghost: true,
            danger: false,
            class: buttonStyles.ghostModifier.default,
        },
        {
            variant: "primary",
            ghost: true,
            danger: false,
            class: buttonStyles.ghostModifier.primary,
        },
        {
            variant: ["default", "primary", "dashed"],
            ghost: true,
            danger: true,
            class: buttonStyles.ghostModifier.danger,
        },
        {
            variant: "destructive",
            ghost: true,
            class: buttonStyles.ghostModifier.danger,
        },
        {
            variant: ["primary", "destructive"],
            ghost: false,
            danger: true,
            class: buttonStyles.dangerModifier.filled,
        },
        {
            variant: ["default", "dashed"],
            ghost: false,
            danger: true,
            class: buttonStyles.dangerModifier.outline,
        },
        {
            variant: "text",
            danger: true,
            class: buttonStyles.dangerModifier.text,
        },
        {
            variant: "link",
            danger: true,
            class: buttonStyles.dangerModifier.link,
        },
    ],
});

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: VariantProps<typeof buttonStyle>["variant"];
    size?: VariantProps<typeof buttonStyle>["size"];
    icon?: React.ReactNode;
    loading?: boolean;
    /** Fills the parent's width. */
    block?: boolean;
    /** Drops the fill onto the border and label. No-op for `text` and `link`. */
    ghost?: boolean;
    /** Recolours the variant red without changing its shape. */
    danger?: boolean;
    asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            children,
            icon = null,
            variant = "default",
            size = "default",
            loading = false,
            disabled = false,
            block = false,
            ghost = false,
            danger = false,
            asChild = false,
            ...props
        },
        ref
    ) => {
        const Comp = asChild ? Slot : "button";

        const renderIcon = loading ? <LoadingOutlined disabled spin /> : icon;
        // A Slot renders an anchor or similar, where neither attribute is valid.
        const buttonOnlyProps = asChild
            ? {}
            : { type: "button" as const, disabled: loading || disabled };

        return (
            <Comp
                ref={ref}
                data-slot="button"
                className={buttonStyle({
                    variant,
                    size,
                    block,
                    ghost,
                    danger,
                    className,
                })}
                {...buttonOnlyProps}
                {...props}
            >
                <>
                    {renderIcon}
                    {children}
                </>
            </Comp>
        );
    }
) as React.ForwardRefExoticComponent<
    ButtonProps & React.RefAttributes<HTMLButtonElement>
> & {
    displayName?: string;
};

Button.displayName = "Button";
